import { FC, useEffect, useState } from "react";
import LoginSignUpSwitch from "@/components/LoginSignUpSwitch";
import { ErrorMessage, Input } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import { useAuth } from "@/data/context/authContext";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import ROUTES from "@/data/Routes";
import { Separator } from "@/components/UI/Separator";
import GoogleAuthButton from "@/components/Auth/GoogleAuthButton";
import Loading from "@/components/Loading";
import Container from "@/components/Container";
import { ArrowRight } from "lucide-react";
import { useFormik } from "formik";
import { z } from "zod";
import { toFormikValidate } from "zod-formik-adapter";
import { REQUIRED_FIELDS_ERROR } from "@/utils/formValidator";
import { ROLES } from "@/data/interfaces/RolesEnum";

const loginSchema = z.object({
  email: z.string().email("El. pašto formatas netinkamas"),
  password: z.string().min(8, "Slaptažodis turi turėti bent 8 simbolius"),
  keepUserLoggedIn: z.boolean(),
});
const Login: FC = () => {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [successMsg, setSuccessMsg] = useState<string | undefined>();
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: searchParams.get("email") ?? "",
      password: "",
      keepUserLoggedIn: false,
    },
    validate: toFormikValidate(loginSchema),
    onSubmit: async (values) => {
      setErrorMsg(undefined);
      setIsRequestInProgress(true);
      try {
        await auth.login(
          values.email,
          values.password,
          values.keepUserLoggedIn,
        );
      } catch (error: any) {
        setErrorMsg(error.message || "Klaida prisijungiant");
      } finally {
        setIsRequestInProgress(false);
      }
    },
  });

  useEffect(() => {
    const resetParam = searchParams.get("reset");
    const emailParam = searchParams.get("email");

    if (resetParam === "success") {
      setSuccessMsg(
        "Slaptažodis sėkmingai pakeistas. Dabar galite prisijungti.",
      );
    }

    if (emailParam) {
      formik.setFieldValue("email", emailParam);
    }
  }, [searchParams]);

  if (auth.user === undefined) {
    return (
      <section className="bg-canvas px-4 pb-12 md:px-6 md:pb-20">
        <div className="flex min-h-screen items-center justify-center">
          <Loading className="mt-34" />
        </div>
      </section>
    );
  }

  if (auth.user) {
    if (
      !auth.user.email_verified_at ||
      (auth.user.role === ROLES.USER &&
        (!auth.user.type_form_submitted || auth.productToRegisterFor))
    ) {
      return <Navigate to={ROUTES.ACCOUNT} replace />;
    }
    if (auth.user.isAdmin) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.HOME} replace />;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const values = formik.values;
    const isEmpty =
      !values.email.trim() &&
      !values.password.trim() &&
      !values.keepUserLoggedIn;
    const errors = await formik.validateForm();
    if (isEmpty) {
      setErrorMsg(REQUIRED_FIELDS_ERROR);
      return;
    }
    if (Object.keys(errors).length) {
      setErrorMsg(Object.values(errors)[0] ?? REQUIRED_FIELDS_ERROR);
      return;
    }
    await formik.handleSubmit(e);
  };

  return (
    <section className="bg-canvas px-4 pb-14 md:px-6 md:pb-20">
      <Container className="pt-25 md:pt-50">
        <div className="text-grey mx-auto w-full max-w-[600px]">
          <div className="mb-9 flex justify-center">
            <LoginSignUpSwitch variant="plain" />
          </div>

          <form
            className="flex w-full flex-col gap-7.5"
            onSubmit={handleSubmit}
            noValidate
          >
            {successMsg && (
              <div className="rounded-[18px] border border-green-200 bg-green-50 px-4 py-3 text-center text-sm text-green-700">
                {successMsg}
              </div>
            )}
            <ErrorMessage error={errorMsg} />
            <div className="flex w-full flex-col gap-[18px]">
              <Input
                label="El. paštas"
                type="email"
                name="email"
                value={formik.values.email}
                autoComplete="email"
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <Input
                label="Slaptažodis"
                type="password"
                name="password"
                value={formik.values.password}
                autoComplete="current-password"
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="flex items-center justify-between gap-4 pt-1">
                <label className="text-ink/90 flex items-center gap-2 text-[15px]">
                  <input
                    type="checkbox"
                    name="keepUserLoggedIn"
                    checked={formik.values.keepUserLoggedIn}
                    onChange={formik.handleChange}
                    className="border-line-strong accent-ink h-4 w-4 rounded-[3px] border bg-white"
                  />
                  Noriu likti prisijungęs
                </label>
                <Link
                  to={`${ROUTES.FORGOT_PASSWORD}${
                    formik.values.email
                      ? `?email=${encodeURIComponent(formik.values.email)}`
                      : ""
                  }`}
                  className="text-muted hover:text-ink text-sm transition-colors"
                >
                  Pamiršau slaptažodį
                </Link>
              </div>
            </div>
            <Button
              variant="ink"
              size="default"
              disabled={isRequestInProgress}
              type="submit"
              className="relative mx-auto h-[58px] w-full max-w-[330px] justify-between rounded-full text-[18px] font-normal"
              onHover
              hoverGradientLight
            >
              Prisijungti
              <ArrowRight className="absolute size-5 opacity-85" />
            </Button>

            <Separator label="arba" labelClassName="my-1 text-muted" />

            <GoogleAuthButton buttonClassName="mx-auto h-[58px] w-full max-w-[330px] rounded-full text-[18px] font-normal md:h-[58px]" />
          </form>
        </div>
      </Container>
    </section>
  );
};

export default Login;
